import CloseIcon from '@mui/icons-material/Close'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import { InputAdornment, MenuItem, TextField } from '@mui/material'
import React, { useEffect, useState } from 'react'
import Drawer from '../../components/Drawer'
import config from '../../config'
import APIInvoke from '../../utils/APIInvoke'
import {
    PAYMENT_METHODS,
    delayBadge,
    isPeso,
    money,
    parseAmount,
    personName,
    todayIso,
} from './collectionFormat'

const selectMenu = { MenuProps: { slotProps: { paper: { className: 'v2-select-menu' } } } }

const PaymentDrawer = ({ recibo, onClose, onPaid }) => {
    const [shown, setShown] = useState(null)
    const active = recibo || shown
    const total = Number(active?.total || 0)
    const [monto, setMonto] = useState(total ? total.toFixed(2) : '')
    const [fecha, setFecha] = useState(todayIso())
    const [metodo, setMetodo] = useState(PAYMENT_METHODS[0])
    const [referencia, setReferencia] = useState('')
    const [notas, setNotas] = useState('')
    const [tipoCambio, setTipoCambio] = useState('')
    const [file, setFile] = useState(null)
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        if (!recibo) return undefined
        setShown(recibo)
        const cambio = recibo.tipoCambio || recibo.poliza?.moneda?.tipoCambio || 1
        setMonto(Number(recibo.total || 0).toFixed(2))
        setFecha(todayIso())
        setMetodo(PAYMENT_METHODS[0])
        setReferencia('')
        setNotas(recibo.observacion || '')
        setTipoCambio(String(cambio || 1))
        setFile(null)
        setError('')
        return undefined
    }, [recibo])

    if (!active) return null

    const poliza = active.poliza || {}
    const cliente = poliza.cliente
    const name = personName(cliente) || 'Asegurado'
    const received = parseAmount(monto)
    const balance = Math.max(total - received, 0)
    const partial = received > 0 && balance > 0.004
    const peso = isPeso(poliza.moneda)
    const badge = delayBadge(active.days)
    const cambio = Number(tipoCambio)

    const uploadProof = async () => {
        if (!file) return
        if (file.size > 10 * 1024 * 1024) throw new Error('El comprobante no puede superar 10 MB.')
        const body = new FormData()
        body.append('bucket', config.bucket.name)
        body.append('path', `recibos/${active.id}/${file.name}`)
        body.append('file', file)
        const uploaded = await (await APIInvoke.uploadStorage(body)).json()
        if (!uploaded?.ok) throw new Error(uploaded?.message || 'No se pudo subir el comprobante.')
        const attached = await (await APIInvoke.invokePOST('/adjuntos', {
            reciboId: active.id,
            dominio: '',
            path: uploaded.body.url,
            archivo: file.name,
        })).json()
        if (!attached?.ok) throw new Error(attached?.message || 'No se pudo guardar el comprobante.')
    }

    const onSubmit = async (event) => {
        event.preventDefault()
        if (!referencia.trim()) {
            setError('La referencia es obligatoria para conciliar el pago.')
            return
        }
        if (!fecha) {
            setError('Indica la fecha de pago.')
            return
        }
        if (partial) {
            setError('El pago parcial no se puede aplicar: el recibo se registra completo.')
            return
        }
        if (!peso && !(cambio > 0)) {
            setError('Indica un tipo de cambio mayor a cero.')
            return
        }
        try {
            setSaving(true)
            setError('')
            await uploadProof()
            const observacion = [notas.trim(), `Forma de pago: ${metodo}.`].filter(Boolean).join(' ')
            const response = await APIInvoke.invokePUT(`/recibos/${active.id}/pagar`, {
                fechaPago: fecha,
                folioPago: referencia.trim(),
                tipoCambio: peso ? Number(active.tipoCambio || poliza.moneda?.tipoCambio || 1) : cambio,
                observacion: observacion.slice(0, 250),
            })
            const payload = await response.json()
            if (!payload?.ok) throw new Error(payload?.message || 'No se pudo registrar el pago.')
            onPaid(active.id, `Pago de ${money(total)} aplicado a ${poliza.noPoliza || 'la póliza'}`)
        } catch (submitError) {
            setError(submitError.message || 'No se pudo registrar el pago.')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Drawer open={Boolean(recibo)} onClose={onClose} bare width={500}>
            <form className="flex h-full flex-col bg-k-surface" onSubmit={onSubmit}>
                <div className="flex items-center justify-between gap-3 border-b border-k-line px-6 py-5">
                    <h2 className="m-0 text-xl font-bold text-k-ink">Registrar pago</h2>
                    <button type="button" aria-label="Cerrar" className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-k-line bg-k-surface text-k-ink hover:bg-k-hover" onClick={onClose}>
                        <CloseIcon style={{ fontSize: 18 }} />
                    </button>
                </div>
                <div className="flex flex-1 flex-col gap-[18px] overflow-y-auto px-6 py-[22px]">
                    <div className="flex flex-col gap-2.5 rounded-xl border border-k-line bg-k-sunken p-4">
                        <div className="flex items-center justify-between gap-2.5">
                            <span className="text-sm font-medium tabular-nums text-k-ink">{poliza.noPoliza || '—'} · Recibo {active.serie}/{active.totalSerie}</span>
                            <span className={`rounded-full px-[9px] py-[3px] text-xs font-semibold ${badge.className}`}>{badge.label}</span>
                        </div>
                        <span className="text-[15px] font-semibold text-k-ink">{name}</span>
                        <div className="flex justify-between text-sm text-k-muted">
                            <span>Importe del recibo</span>
                            <span className="font-medium tabular-nums text-k-ink">{money(total)} {poliza.moneda?.abreviacion || ''}</span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                        <TextField
                            className="v2-field"
                            variant="filled"
                            fullWidth
                            label="Monto recibido"
                            value={monto}
                            onChange={(event) => setMonto(event.target.value)}
                            slotProps={{
                                input: { startAdornment: <InputAdornment position="start">$</InputAdornment> },
                                htmlInput: { inputMode: 'decimal' },
                            }}
                        />
                        <TextField
                            className="v2-field"
                            variant="filled"
                            fullWidth
                            required
                            type="date"
                            label="Fecha de pago"
                            value={fecha}
                            onChange={(event) => setFecha(event.target.value)}
                        />
                    </div>

                    {!peso && (
                        <TextField
                            className="v2-field"
                            variant="filled"
                            fullWidth
                            required
                            label="Tipo de cambio"
                            value={tipoCambio}
                            onChange={(event) => setTipoCambio(event.target.value)}
                            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                        />
                    )}

                    {partial && (
                        <div role="status" className="flex items-start gap-2.5 rounded-xl bg-tone-orange px-3.5 py-3 text-[13px] font-medium text-tone-orange-ink">
                            <WarningAmberOutlinedIcon style={{ fontSize: 16 }} className="mt-px shrink-0" />
                            <span>Pago parcial: el recibo quedaría con un saldo de {money(balance)}. Registra el importe completo para marcarlo como pagado.</span>
                        </div>
                    )}

                    <TextField
                        className="v2-field v2-select"
                        variant="filled"
                        fullWidth
                        select
                        label="Forma de pago"
                        value={metodo}
                        onChange={(event) => setMetodo(event.target.value)}
                        slotProps={{ select: selectMenu }}
                    >
                        {PAYMENT_METHODS.map((label) => <MenuItem key={label} value={label}>{label}</MenuItem>)}
                    </TextField>

                    <TextField
                        className="v2-field"
                        variant="filled"
                        fullWidth
                        required
                        label="Referencia o folio bancario"
                        placeholder="Ej. 0048213397"
                        value={referencia}
                        error={Boolean(error && !referencia.trim())}
                        helperText={error && !referencia.trim() ? error : ''}
                        slotProps={{ htmlInput: { maxLength: 50 } }}
                        onChange={(event) => {
                            setReferencia(event.target.value)
                            setError('')
                        }}
                    />
                    {error && referencia.trim() && (
                        <span role="alert" className="-mt-2.5 flex items-center gap-1.5 text-[13px] font-medium text-k-danger">
                            <ErrorOutlineIcon style={{ fontSize: 14 }} />
                            {error}
                        </span>
                    )}

                    <div className="flex flex-col gap-1.5 text-sm font-semibold text-k-ink">
                        Comprobante
                        <label className="flex cursor-pointer items-center gap-3 rounded-xl border-[1.5px] border-dashed border-k-line-strong bg-k-sunken px-4 py-3.5 text-sm font-normal text-k-muted">
                            <UploadFileOutlinedIcon className="text-k-ink" />
                            <span>
                                <span className="font-semibold text-k-ink underline">{file ? file.name : 'Adjuntar comprobante'}</span>
                                <br />
                                PDF o imagen · máx. 10 MB
                            </span>
                            <input
                                className="sr-only"
                                type="file"
                                accept="application/pdf,image/*"
                                onChange={(event) => setFile(event.target.files?.[0] || null)}
                            />
                        </label>
                    </div>

                    <TextField
                        className="v2-field"
                        variant="filled"
                        fullWidth
                        multiline
                        minRows={3}
                        label="Notas internas"
                        helperText="Opcional"
                        value={notas}
                        onChange={(event) => setNotas(event.target.value)}
                        slotProps={{ htmlInput: { maxLength: 180 } }}
                    />
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2.5 border-t border-k-line px-6 py-4">
                    <span className="text-[13px] text-k-muted">Total a aplicar <span className="font-medium tabular-nums text-k-ink">{money(received || total)}</span></span>
                    <div className="flex gap-2.5">
                        <button type="button" className="h-11 rounded-xl border border-k-line-strong bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover" onClick={onClose} disabled={saving}>Cancelar</button>
                        <button type="submit" className="h-11 rounded-xl bg-k-primary px-[18px] text-sm font-semibold text-white hover:brightness-105 disabled:opacity-60" disabled={saving || partial}>
                            {saving ? 'Registrando…' : 'Registrar pago'}
                        </button>
                    </div>
                </div>
            </form>
        </Drawer>
    )
}

export default PaymentDrawer
